/**
 * Shared WebSocket
 *
 * Обёртка над WebSocket, которая координирует соединения между вкладками.
 * Только "главная" (leader) вкладка держит реальное WebSocket соединение.
 * Остальные вкладки получают данные через Broadcast Channel API.
 *
 * API полностью совместим с обычным WebSocket.
 */

import { TabLeaderElection, createLeaderElection } from './tab-leader-election';
import { showToast } from './toast';
import webSocketManager from './websocket-manager';
import authService from '../services/auth.service';

// Типы сообщений для Broadcast Channel
interface BroadcastMessage {
  type: 'open' | 'message' | 'close' | 'error' | 'send' | 'session_invalidated';
  data?: string | ArrayBuffer | Blob;
  code?: number;
  reason?: string;
  wasClean?: boolean;
}

export class SharedWebSocket {
  // WebSocket-совместимые свойства
  readonly url: string;
  readonly CONNECTING = WebSocket.CONNECTING;
  readonly OPEN = WebSocket.OPEN;
  readonly CLOSING = WebSocket.CLOSING;
  readonly CLOSED = WebSocket.CLOSED;

  // Состояние
  private _readyState: number = WebSocket.CONNECTING;
  private realWebSocket: WebSocket | null = null;
  private leaderElection: TabLeaderElection;
  private channel: BroadcastChannel | null = null;
  private messageBuffer: string[] = [];
  private isLeader = false;
  private autoReconnect: boolean = true;
  private reconnectAttempt: number = 0;
  private reconnectTimer: number | null = null;
  private protocols?: string | string[];
  private isClosed = false;
  private sessionInvalidated = false; // флаг для предотвращения дублирования обработки

  // Event handlers (WebSocket-совместимые)
  onopen: ((this: WebSocket, ev: Event) => void) | null = null;
  onmessage: ((this: WebSocket, ev: MessageEvent) => void) | null = null;
  onerror: ((this: WebSocket, ev: Event) => void) | null = null;
  onclose: ((this: WebSocket, ev: CloseEvent) => void) | null = null;

  constructor(url: string | URL, protocols?: string | string[], options?: { autoReconnect?: boolean }) {
    this.url = typeof url === 'string' ? url : url.toString();
    this.protocols = protocols;
    this.autoReconnect = options?.autoReconnect !== false;

    // Создаём уникальное имя канала на основе URL
    const channelName = `shared_ws_${this.hashUrl(this.url)}`;

    // Инициализируем Leader Election
    this.leaderElection = createLeaderElection(channelName);

    // Инициализируем Broadcast Channel
    try {
      this.channel = new BroadcastChannel(channelName);
      this.channel.onmessage = (event) => this.handleBroadcastMessage(event);
    } catch (error) {
      console.warn('[SharedWebSocket] Broadcast Channel не поддерживается, fallback к обычному WebSocket', error);
      // Fallback - работаем как обычный WebSocket
      this.createRealWebSocket();
      return;
    }

    // Подписываемся на события лидерства
    this.leaderElection.onBecomeLeader(() => {
      this.isLeader = true;
      this.createRealWebSocket();
    });

    this.leaderElection.onLoseLeadership(() => {
      this.isLeader = false;
      this.closeRealWebSocket();
    });

    // Запускаем процесс выборов
    this.leaderElection.start();

    // Регистрируем соединение в глобальном менеджере
    webSocketManager.register(this);
  }

  get readyState(): number {
    if (this.isLeader && this.realWebSocket) {
      return this.realWebSocket.readyState;
    }
    return this._readyState;
  }

  /**
   * Отправить данные через WebSocket
   */
  send(data: string | ArrayBuffer | Blob): void {
    if (this.isLeader && this.realWebSocket) {
      // Лидер отправляет напрямую через реальный WebSocket
      if (this.realWebSocket.readyState === WebSocket.OPEN) {
        this.realWebSocket.send(data);
      } else {
        // Буферизируем, если соединение ещё не открыто
        if (typeof data === 'string') {
          this.messageBuffer.push(data);
        }
      }
    } else {
      // Не-лидеры буферизируют сообщения
      // Они будут отправлены, если эта вкладка станет лидером
      if (typeof data === 'string') {
        this.messageBuffer.push(data);
      }

      // Можно добавить механизм передачи сообщений лидеру через Broadcast Channel
      // Но для большинства случаев буферизация достаточна
    }
  }

  /**
   * Закрыть соединение
   */
  close(code?: number, reason?: string): void {
    this.isClosed = true;
    this.clearReconnectTimer();

    if (this.isLeader && this.realWebSocket) {
      this.realWebSocket.close(code, reason);
    } else {
      // Эмулируем событие закрытия для не-лидеров
      this._readyState = WebSocket.CLOSED;
      if (this.onclose) {
        const closeEvent = new CloseEvent('close', {
          code: code || 1000,
          reason: reason || '',
          wasClean: true,
        });
        this.onclose.call(this as any, closeEvent);
      }
    }

    // Останавливаем Leader Election
    this.leaderElection.stop();

    // Закрываем Broadcast Channel
    if (this.channel) {
      try {
        this.channel.close();
      } catch (error) {
        console.warn('[SharedWebSocket] Ошибка при закрытии Broadcast Channel', error);
      }
      this.channel = null;
    }

    // Отменяем регистрацию в глобальном менеджере
    webSocketManager.unregister(this);
  }

  /**
   * Проверить, является ли текущая вкладка лидером
   */
  isCurrentLeader(): boolean {
    return this.isLeader;
  }

  // Приватные методы

  /**
   * Обработка инвалидации сессии администратором
   */
  private handleSessionInvalidated(): void {
    if (this.sessionInvalidated) {
      return; // Уже обрабатываем
    }

    this.sessionInvalidated = true;
    this.autoReconnect = false; // Отключаем автопереподключение

    console.warn('[SharedWebSocket] Сессия была инвалидирована администратором');

    // Broadcast событие другим вкладкам (только если мы лидер)
    if (this.isLeader) {
      this.broadcast({ type: 'session_invalidated' });
    }

    // Показываем уведомление
    showToast('Ваша сессия была завершена администратором', 'warning', 2000);

    // Закрываем WebSocket соединение
    this.close();

    // Редирект на страницу логина с причиной
    setTimeout(async () => {
      try {
        await authService.logout();
      } catch (error) {
        console.error('[SharedWebSocket] Ошибка при logout', error);
      }
      window.location.href = '/login?reason=session_invalidated';
    }, 2000);
  }

  private createRealWebSocket(): void {
    if (this.isClosed) return;

    if (this.realWebSocket) {
      this.closeRealWebSocket();
    }

    try {
      // Добавляем tab_id и is_leader в URL для координации с бэкендом
      const urlWithParams = this.addTabMetadataToUrl(this.url);

      // Логируем отправляемые протоколы для отладки
      console.log('[SharedWebSocket] Создание WebSocket:', {
        url: urlWithParams,
        protocols: this.protocols,
        isArray: Array.isArray(this.protocols),
      });

      this.realWebSocket = new WebSocket(urlWithParams, this.protocols);

      this.realWebSocket.onopen = (event) => {
        this._readyState = WebSocket.OPEN;
        this.reconnectAttempt = 0; // Сбрасываем счётчик при успешном подключении

        // Логируем успешное подключение и подтвержденный протокол
        console.log('[SharedWebSocket] Соединение установлено:', {
          url: this.url,
          protocol: this.realWebSocket?.protocol,
          readyState: this.realWebSocket?.readyState,
        });

        // Отправляем буферизированные сообщения
        this.flushMessageBuffer();

        // Уведомляем локальные обработчики
        if (this.onopen) {
          this.onopen.call(this.realWebSocket as any, event);
        }

        // Broadcast событие другим вкладкам
        this.broadcast({ type: 'open' });
      };

      this.realWebSocket.onmessage = (event) => {
        // Проверяем на инвалидацию сессии
        try {
          if (typeof event.data === 'string') {
            const data = JSON.parse(event.data);
            if (data.event === 'error' && data.detail === 'session_invalidated') {
              console.warn('[SharedWebSocket] Получено событие session_invalidated');
              this.handleSessionInvalidated();
              return;
            }
          }
        } catch (e) {
          // Не JSON или ошибка парсинга - продолжаем обычную обработку
        }

        // Уведомляем локальные обработчики
        if (this.onmessage) {
          this.onmessage.call(this.realWebSocket as any, event);
        }

        // Broadcast сообщение другим вкладкам
        this.broadcast({
          type: 'message',
          data: event.data,
        });
      };

      this.realWebSocket.onerror = (event) => {
        // Логируем ошибку подключения
        console.error('[SharedWebSocket] Ошибка соединения:', {
          url: this.url,
          protocols: this.protocols,
          readyState: this.realWebSocket?.readyState,
          event,
        });

        // Уведомляем локальные обработчики
        if (this.onerror) {
          this.onerror.call(this.realWebSocket as any, event);
        }

        // Broadcast ошибку другим вкладкам
        this.broadcast({ type: 'error' });
      };

      this.realWebSocket.onclose = (event) => {
        this._readyState = WebSocket.CLOSED;

        // Логируем закрытие соединения с деталями
        console.warn('[SharedWebSocket] Соединение закрыто:', {
          url: this.url,
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          protocols: this.protocols,
        });

        // Проверяем на инвалидацию сессии администратором (fallback если не получили JSON событие)
        if (event.code === 1008 && event.reason === 'Session invalidated by admin') {
          console.warn('[SharedWebSocket] Закрытие из-за инвалидации сессии (код 1008)');
          this.handleSessionInvalidated();
          return;
        }

        // Уведомляем локальные обработчики
        if (this.onclose) {
          this.onclose.call(this.realWebSocket as any, event);
        }

        // Broadcast событие закрытия другим вкладкам
        this.broadcast({
          type: 'close',
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });

        this.realWebSocket = null;

        // Автоматическое переподключение (только если лидер и не закрыт вручную)
        if (this.isLeader && this.autoReconnect && !this.isClosed) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[SharedWebSocket] Ошибка при создании WebSocket', error);

      // Уведомляем об ошибке
      if (this.onerror) {
        const errorEvent = new Event('error');
        this.onerror.call(this as any, errorEvent);
      }
    }
  }

  private closeRealWebSocket(): void {
    if (this.realWebSocket) {
      try {
        if (
          this.realWebSocket.readyState === WebSocket.OPEN ||
          this.realWebSocket.readyState === WebSocket.CONNECTING
        ) {
          this.realWebSocket.close();
        }
      } catch (error) {
        console.warn('[SharedWebSocket] Ошибка при закрытии WebSocket', error);
      }
      this.realWebSocket = null;
    }
  }

  private broadcast(message: BroadcastMessage): void {
    if (!this.channel) return;

    try {
      this.channel.postMessage(message);
    } catch (error) {
      console.error('[SharedWebSocket] Ошибка при отправке broadcast сообщения', error);
    }
  }

  private handleBroadcastMessage(event: MessageEvent<BroadcastMessage>): void {
    // Игнорируем сообщения, если мы лидер (мы сами их отправили)
    if (this.isLeader) return;

    const message = event.data;

    switch (message.type) {
      case 'open':
        this._readyState = WebSocket.OPEN;
        if (this.onopen) {
          const openEvent = new Event('open');
          this.onopen.call(this as any, openEvent);
        }
        break;

      case 'message':
        if (this.onmessage && message.data !== undefined) {
          const messageEvent = new MessageEvent('message', {
            data: message.data,
          });
          this.onmessage.call(this as any, messageEvent);
        }
        break;

      case 'error':
        if (this.onerror) {
          const errorEvent = new Event('error');
          this.onerror.call(this as any, errorEvent);
        }
        break;

      case 'close':
        this._readyState = WebSocket.CLOSED;
        if (this.onclose) {
          const closeEvent = new CloseEvent('close', {
            code: message.code || 1000,
            reason: message.reason || '',
            wasClean: message.wasClean !== undefined ? message.wasClean : true,
          });
          this.onclose.call(this as any, closeEvent);
        }
        break;

      case 'session_invalidated':
        // Обрабатываем инвалидацию сессии на всех вкладках
        console.warn('[SharedWebSocket] Получено broadcast событие session_invalidated');
        this.handleSessionInvalidated();
        break;
    }
  }

  private flushMessageBuffer(): void {
    if (!this.realWebSocket || this.realWebSocket.readyState !== WebSocket.OPEN) {
      return;
    }

    while (this.messageBuffer.length > 0) {
      const message = this.messageBuffer.shift();
      if (message) {
        try {
          this.realWebSocket.send(message);
        } catch (error) {
          console.error('[SharedWebSocket] Ошибка при отправке буферизированного сообщения', error);
          // Возвращаем сообщение в буфер
          this.messageBuffer.unshift(message);
          break;
        }
      }
    }
  }

  private hashUrl(url: string): string {
    // Простой хеш для создания уникального имени канала
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      const char = url.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Добавляет tab_id и is_leader в WebSocket URL как query параметры
   * Это необходимо для координации вкладок на стороне бэкенда
   */
  private addTabMetadataToUrl(baseUrl: string): string {
    try {
      const url = new URL(baseUrl, window.location.origin);
      const tabId = this.leaderElection.getTabId();
      const isLeader = this.leaderElection.isCurrentLeader();

      url.searchParams.set('tab_id', tabId);
      url.searchParams.set('is_leader', isLeader.toString());

      return url.toString();
    } catch (error) {
      console.warn('[SharedWebSocket] Не удалось добавить метаданные в URL, используем исходный URL', error);
      return baseUrl;
    }
  }

  /**
   * Планирует переподключение с экспоненциальной задержкой
   */
  private scheduleReconnect(): void {
    this.clearReconnectTimer();

    // Экспоненциальная задержка: 1s, 2s, 4s, 8s, max 30s
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempt), 30000);
    this.reconnectAttempt++;

    console.log(`[SharedWebSocket] Переподключение через ${delay}ms (попытка ${this.reconnectAttempt})`);

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.isClosed && this.isLeader) {
        this.createRealWebSocket();
      }
    }, delay);
  }

  /**
   * Очищает таймер переподключения
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
